import { match, P } from 'ts-pattern'
import type {
  BeaconEffort,
  CoverageAlarm,
  CoverageAlarmCause,
  CoverageAlarmRemedy,
  CoverageReport,
  CoverageVerdict,
  CoverageVerdictLevel,
} from '../../../utils/api-client/api-schemas.ts'

/**
 * A run's gaps, grouped by why Beacon left something unexamined, so each group can answer the reader's
 * one question: spend more on this repository, or is this simply not something Beacon can examine?
 */

/**
 * Alarms from summaries stored before alarms named their cause land in `other`; parts left unexamined that
 * no alarm names land in `unexamined`.
 */
export type CoverageGapCause = CoverageAlarmCause | 'other' | 'unexamined'

export type CoverageGap = {
  cause: CoverageGapCause
  title: string
  severity: CoverageAlarm['severity']
  /** How many things the gap affects; absent when no alarm in the group says. */
  count?: number
  items: string[]
  sentences: string[]
  nextStep?: string
}

const GAP_ORDER: readonly CoverageGapCause[] = ['budget', 'cap', 'error', 'not_supported', 'not_scannable', 'other']

const GAP_TITLES: Record<CoverageGapCause, string> = {
  budget: 'Ran out of budget',
  cap: 'Hit a scan limit',
  error: 'Something failed',
  not_supported: 'Not supported yet',
  not_scannable: 'Nothing to examine',
  other: 'Other gaps',
  unexamined: 'Not examined',
}

const SEVERITY_RANK: Record<CoverageAlarm['severity'], number> = { high: 3, medium: 2, low: 1 }

const EFFORT_LABELS: Record<BeaconEffort, string> = { low: 'Low', medium: 'Medium', high: 'High' }

const NEXT_EFFORT: Record<BeaconEffort, BeaconEffort | undefined> = { low: 'medium', medium: 'high', high: undefined }

export const VERDICT_STATUS_LABELS: Record<CoverageVerdictLevel, string> = {
  on: 'Complete',
  degraded: 'Gaps',
  off: 'Not examined',
}

/** The verdict's headline; summaries stored before the service wrote one get it from the examined share. */
export const verdictHeadline = (verdict: CoverageVerdict, examinedPercent: number): string =>
  verdict.headline ?? `Beacon examined ${Math.round(examinedPercent)}% of the attack surface`

/** What the reader can do about a gap, or nothing when there is nothing to do. */
export const gapNextStep = ({
  cause,
  remedies,
  effort,
}: {
  cause: CoverageGapCause
  remedies: ReadonlySet<CoverageAlarmRemedy>
  effort: BeaconEffort
}): string | undefined => {
  if (remedies.has('rescan')) {
    return 'Rescan — this was a one-off failure'
  }
  const nextEffort = NEXT_EFFORT[effort]
  return match(cause)
    .with('budget', 'cap', () =>
      match({ canRaiseEffort: remedies.has('raise_effort'), nextEffort })
        .with({ canRaiseEffort: false }, () => 'Raising the effort won’t close this gap')
        .with({ nextEffort: P.nonNullable }, ({ nextEffort: next }) => `Rescan at ${EFFORT_LABELS[next]} effort`)
        .with(
          { nextEffort: undefined },
          () => 'Already at the highest effort — Beacon can’t go further on this repository'
        )
        .exhaustive()
    )
    .with('not_supported', () => 'Beacon doesn’t support this yet')
    .with('not_scannable', () => 'Nothing here Beacon can examine')
    .with('error', 'other', 'unexamined', () => undefined)
    .exhaustive()
}

/** Groups a run's alarms by cause, in the order a reader should weigh them. */
export const groupCoverageGaps = (alarms: readonly CoverageAlarm[], effort: BeaconEffort): CoverageGap[] =>
  GAP_ORDER.flatMap(cause => {
    const inGroup = alarms.filter(alarm => (alarm.cause ?? 'other') === cause)
    const [first] = inGroup
    if (!first) {
      return []
    }
    const counts = inGroup.flatMap(alarm => (alarm.count === undefined ? [] : [alarm.count]))
    return [
      {
        cause,
        title: GAP_TITLES[cause],
        severity: inGroup.reduce(
          (worst, alarm) => (SEVERITY_RANK[alarm.severity] > SEVERITY_RANK[worst] ? alarm.severity : worst),
          first.severity
        ),
        count: counts.length ? counts.reduce((total, count) => total + count, 0) : undefined,
        items: [...new Set(inGroup.flatMap(alarm => alarm.items ?? []))],
        sentences: inGroup.map(alarm => alarm.sentence),
        nextStep: gapNextStep({ cause, remedies: new Set(inGroup.flatMap(alarm => alarm.remedy ?? [])), effort }),
      },
    ]
  })

/**
 * The entry points, sensitive assets, checkable controls and repository-wide checks Beacon left unexamined
 * that no alarm already names, each with why, so a run short of 100% always says what it missed.
 */
export const unexaminedGap = (coverage: CoverageReport): CoverageGap | undefined => {
  const named = new Set(coverage.proof.alarms.flatMap(alarm => alarm.items ?? []))
  const missed = [
    ...coverage.entry_points,
    ...coverage.assets,
    ...coverage.controls.filter(control => control.auditable),
    coverage.repository,
  ].filter(entity => (entity.status === 'not_examined' || entity.status === 'partly') && !named.has(entity.name))
  return missed.length > 0
    ? {
        cause: 'unexamined',
        title: GAP_TITLES.unexamined,
        severity: 'medium',
        count: missed.length,
        items: [],
        sentences: missed.map(entity => `${entity.name}: ${entity.reason || 'Beacon did not say why.'}`),
      }
    : undefined
}

/** Every gap in a run's coverage: its alarms grouped by cause, then whatever else it left unexamined. */
export const coverageGaps = (coverage: CoverageReport, effort: BeaconEffort): CoverageGap[] => {
  const unexamined = unexaminedGap(coverage)
  return [...groupCoverageGaps(coverage.proof.alarms, effort), unexamined].filter(gap => gap !== undefined)
}
